import { useState } from 'react';

import { PASS_CATEGORIES } from '@/lib/passes-format';
import { Button } from '@/components/ui/button';
import { Chip } from '@/components/ui/chip';
import { Icon } from '@/components/ui/icon';
import { Spinner } from '@/components/ui/spinner';

export interface BrowseParams {
  category: string;
  gps: string;
  /** RFC3339 visit timestamp. */
  date: string;
}

/** GPS as "<lat>, <lng>" (6dp, comma+space) — same wire format the rides flow uses. */
function toGps(lat: number, lng: number): string {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

/** datetime-local default ~1h out, in the viewer's local time (minute precision). */
function defaultVisitLocal(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Category-first discovery: pick an experience type, a coarse "near me" location and a
 * visit time, then Browse. No map (entry-pass discovery is category-led; a map would
 * pull ~550KB of mapbox-gl onto this route for a single search-center).
 */
export function CategoryPicker({
  onBrowse,
  pending,
}: {
  onBrowse: (params: BrowseParams) => void;
  pending: boolean;
}) {
  const [category, setCategory] = useState(PASS_CATEGORIES[0]?.code ?? 'MUSEUM');
  const [gps, setGps] = useState('');
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState('');
  const [visitLocal, setVisitLocal] = useState(defaultVisitLocal);

  function useMyLocation() {
    if (!navigator.geolocation) {
      setLocError('Location is not available in this browser.');
      return;
    }
    setLocating(true);
    setLocError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps(toGps(pos.coords.latitude, pos.coords.longitude));
        setLocating(false);
      },
      () => {
        setLocError("Couldn't get your location — browsing city-wide.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  }

  function browse() {
    // A datetime-local input can be cleared/partial → new Date('') is Invalid Date and
    // .toISOString() throws. Fall back to a valid default so we never crash the click and
    // always send a well-formed RFC3339 (the backend rejects malformed visit dates).
    const parsed = new Date(visitLocal);
    const date = Number.isNaN(parsed.getTime())
      ? new Date(Date.now() + 60 * 60 * 1000).toISOString()
      : parsed.toISOString();
    onBrowse({ category, gps, date });
  }

  return (
    <div className="wb-card wb-card--pad wb-col wb-gap-16">
      <div className="wb-col wb-gap-8">
        <p className="wb-label" style={{ margin: 0 }}>
          What are you in the mood for?
        </p>
        <div className="wb-row wb-wrap wb-gap-8">
          {PASS_CATEGORIES.map((c) => (
            <Chip key={c.code} active={category === c.code} onClick={() => setCategory(c.code)}>
              {c.label}
            </Chip>
          ))}
        </div>
      </div>

      <div className="wb-col wb-gap-12">
        <div className="wb-col wb-gap-4">
          <span className="wb-label" style={{ margin: 0 }}>
            Location
          </span>
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating}
            className={`wb-field-btn ${gps ? '' : 'is-empty'}`}
          >
            {locating ? (
              <Spinner />
            ) : (
              <Icon name="pin" size={20} style={{ color: 'var(--cat-pass)' }} />
            )}
            <span>{gps ? 'Using your location' : 'Use my location'}</span>
          </button>
        </div>

        <div className="wb-col wb-gap-4">
          <label className="wb-label" htmlFor="visit-when" style={{ margin: 0 }}>
            Visit date &amp; time
          </label>
          <input
            id="visit-when"
            type="datetime-local"
            value={visitLocal}
            onChange={(e) => setVisitLocal(e.target.value)}
            className="wb-field"
          />
        </div>
      </div>

      {locError && <p className="wb-tiny">{locError}</p>}

      <Button variant="primary" block onClick={browse} disabled={pending}>
        {pending ? 'Searching…' : 'Browse experiences'}
      </Button>
    </div>
  );
}
